import React, { useEffect, useState, useMemo } from 'react';
import { Sparklines, SparklinesLine } from "react-sparklines";
import Footer from './Footer';
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";

import { getCurrencySymbol } from "./utils";
import api from "./context/api";
import { useDevice } from "./context/DeviceContext";
import { useAuth } from "./context/AuthContext";

function Dashboard() {
  const [industries, setIndustries] = useState({});
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(true);
  const [sortConfigs, setSortConfigs] = useState({});
  const [activeType, setActiveType] = useState(null);
  const [weighted, setWeighted] = useState(true);
  const [categoryView, setCategoryView] = useState("sector"); // "sector" | "holding"
  const [asOfInput, setAsOfInput] = useState(''); // html date input
  const TIMEFRAMES = ['vs52WH','1D','1W','1M','3M','6M','1Y'];
  const TIMEFRAME_SECTOR = {
    "CHEMICAL" : ['vs52WH', '1W', '1M', '3M', '6M', '1Y'],
    "UPSTREAM" : ['vs52WH', '1W', '1M', '3M', '6M', '1Y'],
  }
  const TYPE_ORDER = [
    "EQUITY",
    "INDIA_INDEX",
    "GLOBAL_STOCKS",
    "GLOBAL_INDEX",
    "CRYPTO",
    "USD",
    "INR",
    "COMMODITY",
    "AGRI",
    "UPSTREAM",
    "CHEM_INDIA",
    "CHEM_ASIA",
    "DATA_ALL"
  ];
  const { isMobile } = useDevice();
  const { user, initializing } = useAuth();

  // Correlation Table States
  const [showCorrelation, setShowCorrelation] = useState(false);
  const [correlationData, setCorrelationData] = useState(null);
  const [corrWindow, setCorrWindow] = useState('30D'); // 30D | 60D | 90D

  const makeInitialFilters = () => ({
    vs52WH: { op: '>', value: '' },
    '1D':   { op: '>', value: '' },
    '1W':   { op: '>', value: '' },
    '1M':   { op: '>', value: '' },
    '3M':   { op: '>', value: '' },
    '6M':   { op: '>', value: '' },
    '1Y':   { op: '>', value: '' },
  });

  const countFiltered = (stocks) => {
    return stocks.filter(stockPassesFilters).length;
  };

  const makeInitialMcapFilter = () => ({ op: ">", value: "" }); // value in Cr

  const [foOnly, setFoOnly] = useState(false);
  const [mcapFilter, setMcapFilter] = useState(makeInitialMcapFilter());
  const [mcapDraft, setMcapDraft] = useState(makeInitialMcapFilter());

  // active filters (used for filtering the list)
  const [filters, setFilters] = useState(makeInitialFilters());

  // drafts (UI inputs; only copied into `filters` when Apply is clicked)
  const [filterDrafts, setFilterDrafts] = useState(makeInitialFilters());

  // helper: compare number with op + ref
  const cmp = (num, op, ref) => {
    if (ref === '' || ref === null || Number.isNaN(ref)) return true; // no filter
    return op === '>' ? num > ref : num < ref;
  };

  // helper: does a stock pass all active filters?
  const stockPassesFilters = (stock) => {
    for (const k of TIMEFRAMES) {
      const { op, value } = filters[k] || {};
      if (value === '' || value === null) continue; // inactive
      const n = toNum(stock?.dummyData?.[k]);       // "12.34%" -> 12.34
      const ref = parseFloat(value);
      if (n == null || Number.isNaN(ref)) return false;
      if (!cmp(n, op || '>', ref)) return false;
    }

    // --- NEW: Market Cap filter (Cr) for EQUITY only
    if (activeType === "EQUITY") {
      const { op, value } = mcapFilter || {};
      if (value !== "" && value !== null) {
        const ref = parseFloat(value);
        const mcapCr = toNum(stock?.marketCap) != null ? (toNum(stock.marketCap) / 1e7) : null; // marketCap -> Cr

        if (mcapCr == null || Number.isNaN(ref)) return false;
        if (!cmp(mcapCr, op || ">", ref)) return false;
      }
    }

    // --- NEW: F&O only filter
    if (activeType === "EQUITY" && foOnly) {
      const pcrOi = Number(stock?.pcr_oi ?? 0);
      if (!Number.isFinite(pcrOi) || pcrOi <= 0) return false;
    }

    return true;
  };

  const fetchIndustries = async (asOfStr = '') => {
    try {
      setLoading(true);
      // Convert YYYY-MM-DD -> DD-MM-YYYY for the API query as requested
      const asOfParam = asOfStr
        ? (() => {
            const [y, m, d] = asOfStr.split('-');
            return `${d}-${m}-${y}`;
          })()
        : '';
      const url = asOfParam
        ? `${process.env.REACT_APP_API_URL}/api/industries?asOf=${asOfParam}`
        : `${process.env.REACT_APP_API_URL}/api/industries`;

      const res = await api.get(url);

      // NEW: support { industries, sectorCorrelation } shape
      const apiIndustries = res.data.industries || res.data;
      const apiCorrelation = res.data.sectorCorrelation || null;

      setIndustries(apiIndustries);
      setCorrelationData(apiCorrelation);

      const types = Object.values(apiIndustries).map(i => i.type || 'Other');
      const defaultType =  "EQUITY";
      setActiveType(defaultType);
      const initialSorts = {};
      types.forEach(type => {
        initialSorts[type] = { field: '1D', direction: 'desc' };
      });
      setSortConfigs(initialSorts);
    } catch (error) {
      console.error('Error fetching data', error);
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    if (initializing) return;

    fetchIndustries();
  }, [initializing, user]);

  // pass: buildExportRows()            -> exports stocks (current behavior)
  // pass: buildExportRows("sectors")   -> exports top-level sectors only
  const buildExportRows = (mode) => {
    const rows = [];
    const entries = Object.entries(industries)
      .filter(([, data]) => !activeType || data.type === activeType);

    const toNum = (v) => {
      if (v == null) return -Infinity;
      const s = String(v);
      if (s.endsWith('%')) {
        const n = parseFloat(s.slice(0, -1));
        return Number.isNaN(n) ? -Infinity : n;
      }
      const n = parseFloat(s);
      return Number.isNaN(n) ? -Infinity : n;
    };

    if (mode === "sectors") {
      // ---- sector-level export (one row per sector) ----
      for (const [industry, data] of entries) {
        const stocks = data.stocks || [];

        const totalMcap = stocks.reduce((acc, s) => acc + (s.marketCap || 0), 0);
        const latestUpdate = stocks.reduce((latest, s) => {
          const d = s?.lastUpdateDate ? new Date(s.lastUpdateDate) : null;
          if (!d || isNaN(d)) return latest;
          return !latest || d > latest ? d : latest;
        }, null);

        const wr = data.weightedReturns || {};

        rows.push({
          Industry: industry,
          Type: data.type || '',
          Stocks: stocks.length,
          MarketCapCr: totalMcap ? (totalMcap / 1e7).toFixed(2) : '',
          '1D': wr['1D'] ?? '',
          '1W': wr['1W'] ?? '',
          '1M': wr['1M'] ?? '',
          '3M': wr['3M'] ?? '',
          '6M': wr['6M'] ?? '',
          '1Y': wr['1Y'] ?? '',
          vs52WH: wr['vs52WH'] ?? '',
          LastUpdate: latestUpdate ? latestUpdate.toISOString().slice(0, 10) : '',
        });
      }
    } else {
      // ---- stock-level export (existing behavior) ----
      for (const [industry, data] of entries) {
        for (const s of (data.stocks || [])) {
          rows.push({
            Industry: industry,
            Symbol: s.symbol,
            Name: s.name,
            Price: Number.isFinite(s.price) ? s.price.toFixed(2) : '',
            PE: Number.isFinite(s.pe) ? s.pe.toFixed(2) : '',
            ROE: typeof s.roe === 'number' ? (s.roe * 100).toFixed(2) + '%' : '',
            MarketCapCr: Number.isFinite(s.marketCap) ? (s.marketCap / 1e7).toFixed(2) : '',
            '1D': s?.dummyData?.['1D'] ?? '',
            '1W': s?.dummyData?.['1W'] ?? '',
            '1M': s?.dummyData?.['1M'] ?? '',
            '3M': s?.dummyData?.['3M'] ?? '',
            '6M': s?.dummyData?.['6M'] ?? '',
            '1Y': s?.dummyData?.['1Y'] ?? '',
            vs52WH: s?.dummyData?.['vs52WH'] ?? '',
            LastUpdate: s?.lastUpdateDate ?? '',
          });
        }
      }
    }

    // Sort using the current UI config (works for both modes)
    const cfg = sortConfigs[activeType];
    if (cfg?.field) {
      const f = cfg.field;
      rows.sort((a, b) => {
        const av = toNum(a[f]);
        const bv = toNum(b[f]);
        return cfg.direction === 'asc' ? av - bv : bv - av;
      });
    }

    return rows;
  };

  const toCSV = (rows) => {
    if (!rows.length) return '';
    const headers = Object.keys(rows[0]);
    const esc = (v) => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    return [headers.join(','), ...rows.map(r => headers.map(h => esc(r[h])).join(','))].join('\n');
  };

  const handleExportCSV = (mode) => {
    const rows = buildExportRows(mode);
    const csv = toCSV(rows);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const asOfPart = asOfInput ? `_${asOfInput}` : '';
    const fname = `industries_${activeType || 'all'}_${mode || 'symbols'}_${asOfPart}.csv`;
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = fname;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const groupedByType = useMemo(() => {
    const groups = {};
    Object.entries(industries).forEach(([industry, data]) => {
      const type = data.type || 'Other';
      if (!groups[type]) groups[type] = {};
      groups[type][industry] = data;
    });
    return groups;
  }, [industries]);

  const toggleExpand = (industry) => {
    setExpanded((prev) => {
      const currentType = activeType || 'Other';
      const updatedForType = {
        ...(prev[currentType] || {}),
        [industry]: !prev?.[currentType]?.[industry]
      };
      return { ...prev, [currentType]: updatedForType };
    });
  };

  const expandAllIndustries = () => {
    const industriesOfType = Object.keys(groupedByType[activeType] || {});
    const expandedMap = {};
    industriesOfType.forEach(ind => expandedMap[ind] = true);
    setExpanded(prev => ({ ...prev, [activeType]: expandedMap }));
  };

  const collapseAllIndustries = () => {
    setExpanded(prev => ({ ...prev, [activeType]: {} }));
  };

  const toggleSort = (type, field) => {
    setSortConfigs(prev => {
      const current = prev[type] || { field: '1D', direction: 'desc' };
      const newDirection = current.field === field && current.direction === 'desc' ? 'asc' : 'desc';
      return {
        ...prev,
        [type]: { field, direction: newDirection }
      };
    });
  };

  const formatIndustryName = (str) => {
    if (!str) return "";

    // ✅ holding group keys: group_MukeshAmbani -> Mukesh Ambani
    const cleaned = str.startsWith("group_") ? str.slice("group_".length) : str;

    return cleaned
      .replace(/_/g, " ")
      .replace(/([a-z])([A-Z])/g, "$1 $2");
  };

  const getColorStyle = (value) => {
    if (!value || value === 'N/A') return { color: 'gray' };
    const number = parseFloat(value);
    if (isNaN(number)) return { color: 'gray' };
    return number >= 0 ? { color: 'green' } : { color: 'red' };
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return isNaN(date) ? 'Invalid date' : date.toLocaleDateString('en-IN');
  };

  const formatDateTime = (dateStr) => {
    const date = new Date(dateStr);
    if (isNaN(date)) return "Invalid date";

    const day = date.toLocaleDateString("en-IN", {
      day: "2-digit",
    });

    const month = date.toLocaleDateString("en-IN", {
      month: "2-digit",
    });

    const year = date.getFullYear();

    const hours = date.getHours();
    const minutes = date.getMinutes();

    // 👇 Detect exactly 12:00 AM
    if (hours === 0 && minutes === 0) {
      return `${day}/${month}/${year} - closing`;
    }

    const time = date
      .toLocaleTimeString("en-IN", {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      })
      .toLowerCase();

    return `${day}/${month}/${year} ${time}`;
  };



  // parse "12.34%" or "12.34" -> number, else null
  const toNum = (v) => {
    if (v == null) return null;
    if (typeof v === 'number') return Number.isFinite(v) ? v : null;

    // Normalize: unicode minus → ASCII hyphen, remove commas
    const s = String(v).trim().replace(/\u2212/g, '-').replace(/,/g, '');

    // Grab the first signed number anywhere in the string
    const m = s.match(/-?\d+(\.\d+)?/);
    return m ? parseFloat(m[0]) : null;
  };

  // All-symbols counts for the active type (>= 0 is positive)
  const globalCounts = useMemo(() => {
    const counts = Object.fromEntries(
      TIMEFRAMES.map(tf => [tf, { pos: 0, neg: 0 }])
    );

    const seen = new Set();

    const list = Object.values(groupedByType[activeType] || {});

    for (const ind of list) {
      for (const s of (ind.stocks || [])) {
        if (seen.has(s.symbol)) continue;
        seen.add(s.symbol);

        if (!stockPassesFilters(s)) continue;

        for (const tf of TIMEFRAMES) {
          const n = toNum(s?.dummyData?.[tf]);
          if (n == null) continue;
          if (n >= 0) counts[tf].pos++;
          else counts[tf].neg++;
        }
      }
    }

    return counts;
  }, [groupedByType, activeType, filters, mcapFilter, foOnly]); // ✅ add foOnly

  // Per-sector counts (memoized map: { [industry]: { [tf]: {pos,neg} } })
  const sectorCounts = useMemo(() => {
    const map = {};
    const byType = groupedByType[activeType] || {};
    for (const [industry, data] of Object.entries(byType)) {
      const c = Object.fromEntries(TIMEFRAMES.map(tf => [tf, { pos: 0, neg: 0 }]));
      for (const s of (data.stocks || [])) {
        for (const tf of TIMEFRAMES) {
          const n = toNum(s?.dummyData?.[tf]);
          if (n == null) continue;
          if (n >= 0) c[tf].pos++; else c[tf].neg++;
        }
      }
      map[industry] = c;
    }
    return map;
  }, [groupedByType, activeType]);

  const orderedTypes = useMemo(() => {
    const availableTypes = Object.keys(groupedByType);

    return [
      // preferred order
      ...TYPE_ORDER.filter(type => availableTypes.includes(type)),
      // any unknown / future types (kept at the end)
      ...availableTypes.filter(type => !TYPE_ORDER.includes(type)),
    ];
  }, [groupedByType]);


  const cleanSectorName = (name) =>
    name.replace(/_SECTOR$/i, "").replace(/_/g, " ");

  const industryDataList = Object.values(groupedByType[activeType] || {});
  // const lastUpdateDate = industryDataList[0]?.stocks?.[0]?.lastUpdateDate || null;

  const lastUpdateDate = new Date(
    Math.max(
      ...industryDataList.flatMap(ind =>
        ind.stocks
          .map(s => new Date(s.lastUpdateDate))
          .filter(d => !isNaN(d))   // ✅ remove invalid dates
      )
    )
  );

  const totalSymbols = new Set(
    industryDataList.flatMap(industryData =>
      industryData.stocks
        .filter(stockPassesFilters)   // ✅ only include visible stocks
        .map(stock => stock.symbol)
    )
  ).size;

  const getTimeframesForType = (activeType) => {
    return TIMEFRAME_SECTOR?.[activeType] ?? TIMEFRAMES;
  };

  const renderCorrelationTable = () => {
    if (!correlationData) {
      return (
        <div className="mt-4 text-center text-sm text-gray-500">
          No correlation data available.
        </div>
      );
    }

    const matrix = correlationData[corrWindow] || {};
    const symbols = Object.keys(matrix);


    if (symbols.length === 0) {
      return (
        <div className="mt-4 text-center text-sm text-gray-500">
          No correlation data for {corrWindow}.
        </div>
      );
    }

    const sortedSymbols = [...symbols].sort();

    const cellBg = (v) => {
      if (v == null || isNaN(v)) return "";
      if (v >= 0.8) return "bg-green-300";
      if (v >= 0.5) return "bg-green-200";
      if (v >= 0.3) return "bg-green-100";
      if (v <= -0.8) return "bg-red-300";
      if (v <= -0.5) return "bg-red-200";
      if (v <= -0.3) return "bg-red-100";
      return "";
    };

    return (
      <div className="mt-4">
        {/* Header row with dropdown + back button */}
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <h2 className="text-lg font-semibold">
            Sector Correlation – {corrWindow}
          </h2>

          <div className="flex items-center gap-2 ml-auto">
            <select
              className="border px-2 py-1 rounded text-sm"
              value={corrWindow}
              onChange={(e) => setCorrWindow(e.target.value)}
            >
              <option value="30D">30 Days</option>
              <option value="60D">60 Days</option>
              <option value="90D">90 Days</option>
            </select>

          </div>
        </div>

        <div className="overflow-auto border rounded">
          <table className="table-auto border-collapse text-sm">
            <thead>
              <tr>
                {/* top-left corner cell: sticky both top & left */}
                <th
                  className="p-2 bg-gray-100 sticky left-0 top-0 z-30 text-left"
                >
                  Correlations
                </th>

                {sortedSymbols.map((s) => (
                  <th
                    key={s}
                    title={cleanSectorName(s)}
                    className="px-1 py-0.5 sticky top-0 bg-gray-100 text-[10px] z-20 align-bottom
                              border-r border-gray-300"
                    style={{ minWidth: "40px", maxWidth: "40px", height: "110px" }}
                  >
                    <div className="relative w-full h-full">
                      <div
                        className="absolute bottom-1 left-2 origin-bottom-left whitespace-nowrap"
                        style={{ transform: "rotate(-60deg)" }}
                      >
                        {cleanSectorName(s)}
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {sortedSymbols.map((row) => (
                <tr key={row}>
                  <td className="p-2 bg-gray-50 text-[10px] sticky left-0 z-10">
                    <a
                      href={`symbol/${encodeURIComponent(row.toUpperCase().replace(/ /g, "_"))}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600"
                    >    
                      {cleanSectorName(row)}    {/* vertical axis without _SECTOR */}
                    </a>
                  </td>
                  {sortedSymbols.map((col) => {
                    const v = matrix[row]?.[col];
                    return (
                      <td
                        key={col}
                        className={`px-1 py-0.5 text-center text-[10px] leading-tight ${cellBg(v)}`}
                        style={{ minWidth: "32px", maxWidth: "32px" }}
                        title={`${cleanSectorName(row)} ↔ ${cleanSectorName(col)} : ${
                          v == null || isNaN(v) ? "N/A" : v.toFixed(2)
                        }`}
                      >
                        {v == null || isNaN(v) ? "-" : v.toFixed(2)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <>
    <div className="p-4 mb-12">
      {loading ? (
        <div className="text-center p-10">
          <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
          <div className="mt-4 text-blue-700 font-semibold">Loading...</div>
        </div>
      ) : Object.keys(groupedByType).length === 0 ? (
        <div className="text-center text-red-500 font-semibold">No data available.</div>
      ) : (
        <div>
          {/* Top Tabs */}
          <div className="flex flex-wrap gap-2 border-b mb-2 pb-1">
            {orderedTypes.map((type) => (
              <a
                key={type}
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  setActiveType(type);
                  setExpanded((prev) => ({ ...prev, [type]: {} }));
                  setShowCorrelation(false);
                }}
                className={`px-3 py-1 text-sm font-medium transition border-b-2 ${
                  activeType === type
                    ? "border-blue-600 text-blue-600 font-semibold"
                    : "border-transparent text-gray-500 hover:text-blue-500 hover:border-blue-300"
                }`}
                style={{ minWidth: "fit-content" }}
              >
                {type.replace(/_/g, " ")}
              </a>
            ))}
          </div>

          {/* Info + Buttons */}
          <div className="text-sm text-gray-500 flex flex-col sm:flex-row justify-between items-start sm:items-center mb-2 gap-2">
          <div className="text-gray-400">
            {(activeType === 'EQUITY' || activeType === 'INDIA_INDEX') ? (
              <>
                Updates every 30 min, last updated - {formatDateTime(lastUpdateDate)}. 
                Tracked Symbols - {totalSymbols}
              </>
            ) : activeType === 'GLOBAL_STOCKS' ? (
              <>
                Updates Daily 9 AM, last updated - {formatDate(lastUpdateDate)}. 
                Tracked Symbols - {totalSymbols}
              </>
            ) : (
              <>
                Updates Daily 11 PM, last updated - {formatDate(lastUpdateDate)}. 
                Tracked Symbols - {totalSymbols}
              </>
            )}
          </div>
            <div className="flex flex-wrap items-center gap-2">

              {/* --- ADD: Date Picker + Go / Clear */}
              <div className="flex items-center gap-2">
                <span className="font-bold text-black">History Date:</span>
                <input
                  type="date"
                  value={asOfInput}
                  onChange={(e) => setAsOfInput(e.target.value)}
                  className="px-2 py-1 text-xs border rounded"
                  aria-label="As of date"
                />
                <button
                  onClick={() => asOfInput && fetchIndustries(asOfInput)}
                  className="px-3 py-1 text-xs border rounded bg-indigo-600 text-white hover:bg-indigo-700"
                >
                  Go
                </button>
                <button
                  onClick={() => { setAsOfInput(''); fetchIndustries(); }}
                  className="px-3 py-1 text-xs border rounded bg-gray-100 hover:bg-gray-200"
                >
                  Clear
                </button>
              </div>

              <div className={isMobile ? "grid grid-flow-col auto-cols-max gap-2" : "inline-flex gap-2 flex-nowrap"}>
                <button
                  onClick={() => handleExportCSV()}
                  className={`flex-none ${isMobile ? 'px-2 py-1 text-[11px]' : 'px-3 py-1 text-xs'} border rounded bg-emerald-600 text-white hover:bg-emerald-700`}
                >
                  Export Symbols
                </button>

                <button
                  onClick={() => handleExportCSV('sectors')}
                  className={`flex-none ${isMobile ? 'px-2 py-1 text-[11px]' : 'px-3 py-1 text-xs'} border rounded bg-emerald-600 text-white hover:bg-emerald-700`}
                >
                  Export Sectors
                </button>
              </div>

            </div>
          </div>

          <div>
            {activeType === "EQUITY" && (
              <div className="flex items-center gap-2 mb-1 w-full">
                {/* Left group: keep both switches together */}
                <div className="flex items-center gap-2">
                  {/* Weight mode */}
                  <div className="inline-flex rounded-lg border border-gray-300 bg-white overflow-hidden">
                    <button
                      onClick={() => setWeighted(true)}
                      className={`px-3 py-1 text-xs font-semibold transition ${
                        weighted
                          ? "bg-blue-600 text-white"
                          : "bg-white text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      Weighted
                    </button>

                    <div className="w-px bg-gray-300" />

                    <button
                      onClick={() => setWeighted(false)}
                      className={`px-3 py-1 text-xs font-semibold transition ${
                        !weighted
                          ? "bg-blue-600 text-white"
                          : "bg-white text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      Equal
                    </button>
                  </div>

                  {/* Sector / Holding switch */}
                  <div className="inline-flex rounded-lg border border-gray-300 bg-white overflow-hidden">
                    <button
                      onClick={() => {
                        setCategoryView("sector");
                        setExpanded((prev) => ({ ...prev, [activeType]: {} }));
                      }}
                      className={`px-3 py-1 text-xs font-semibold transition ${
                        categoryView === "sector"
                          ? "bg-slate-800 text-white"
                          : "bg-white text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      Sector
                    </button>

                    <div className="w-px bg-gray-300" />

                    <button
                      onClick={() => {
                        setCategoryView("holding");
                        setExpanded((prev) => ({ ...prev, [activeType]: {} }));
                      }}
                      className={`px-3 py-1 text-xs font-semibold transition ${
                        categoryView === "holding"
                          ? "bg-slate-800 text-white"
                          : "bg-white text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      Holding
                    </button>
                  </div>
                </div>

                {/* Right: Correlation */}
                <button
                  onClick={() => setShowCorrelation((p) => !p)}
                  className={`ml-auto px-3 py-1 text-xs font-semibold rounded-lg border transition
                    ${
                      showCorrelation
                        ? "bg-purple-600 text-white border-purple-600"
                        : "bg-white text-purple-700 border-purple-300 hover:bg-purple-50"
                    }`}
                  title="Toggle correlation table"
                >
                  {isMobile 
                  ? 
                  showCorrelation ? "Corre: ON" : "Corre"
                  :
                  showCorrelation ? "Correlations: ON" : "Correlations"
                  }
                  
                </button>
              </div>
            )}
          </div>

          {/* Table */}
          {showCorrelation ? (
            renderCorrelationTable()
          ) : (
            <div className="overflow-x-auto">
              <table className="table-auto w-full border-collapse text-sm text-gray-800">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="sticky left-0 top-0 bg-gray-100 z-30 p-2 w-[70px] min-w-[70px] max-w-[70px]">#</th>
                    <th
                      className={`sticky left-[70px] top-0 bg-gray-100 z-30 p-2 text-left cursor-pointer ${
                        isMobile ? "min-w-[200px] max-w-[200px]" : "min-w-[250px] max-w-[250px]"
                      }`}
                    >
                    <div className="flex items-center gap-1 py-1">

                      <button
                        onClick={() => toggleSort(activeType, 'Industry')}
                        className="flex items-center gap-1 text-sm font-semibold text-gray-800 hover:text-blue-600 transition"
                      >
                        <span>Category</span>

                        {sortConfigs[activeType]?.field === "Industry" && (
                          <span className="text-[10px] font-bold text-blue-600 leading-none">
                            {sortConfigs[activeType].direction === "asc" ? "↑" : "↓"}
                          </span>
                        )}
                      </button>

                      {/* Expand / Collapse all */}
                      <div className="ml-1 inline-flex items-center overflow-hidden rounded-md border border-gray-300 bg-white">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            expandAllIndustries();
                          }}
                          className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-green-700 hover:bg-green-50 transition leading-none"
                          title="Expand all categories"
                        >
                          <span className="text-sm leading-none">+</span>
                          <span>All</span>
                        </button>

                        <div className="h-4 w-px bg-gray-300" />

                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            collapseAllIndustries();
                          }}
                          className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 transition leading-none"
                          title="Collapse all categories"
                        >
                          <span className="text-sm leading-none">−</span>
                          <span>All</span>
                        </button>
                      </div>
                    </div>

                      {activeType === "EQUITY" && (
                        <div>
                        <div
                          className="mt-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {/* Row 1: label + select + input */}
                          <div className="flex items-center gap-1 mt-2">
                            <span className="text-xs font-medium font-semibold text-gray-500">MCap Cr</span>

                            <select
                              className="border rounded px-1 py-0.5 text-xs"
                              value={mcapDraft.op}
                              onChange={(e) =>
                                setMcapDraft((p) => ({ ...p, op: e.target.value }))
                              }
                              title="Market Cap operator"
                            >
                              <option value=">">&gt;</option>
                              <option value="<">&lt;</option>
                            </select>

                            <input
                              type="number"
                              step="1"
                              placeholder="Cr"
                              className="w-14 border rounded px-1 py-0.5 text-xs"
                              value={mcapDraft.value}
                              onChange={(e) =>
                                setMcapDraft((p) => ({ ...p, value: e.target.value }))
                              }
                              title="Market Cap in Cr"
                            />
                          </div>

                          {/* Row 2: buttons */}
                          <div className="mt-1 flex items-center gap-1">
                            <button
                              onClick={() => setMcapFilter({ ...mcapDraft })}
                              className={`px-2 py-1 text-xs border rounded text-white transition-colors
                                ${mcapFilter.value ? "bg-blue-800 hover:bg-blue-700" : "bg-blue-400 hover:bg-blue-700"}
                              `}
                              title="Apply Market Cap filter"
                            >
                              ✓
                            </button>

                            <button
                              onClick={() => {
                                setMcapDraft(makeInitialMcapFilter());
                                setMcapFilter(makeInitialMcapFilter());
                              }}
                              className="px-2 py-1 text-xs border rounded bg-red-400 text-white hover:bg-red-700"
                              title="Clear Market Cap filter"
                            >
                              ✕
                            </button>
                          </div>
                        </div>

                        {/* F&O Only Filter */}
                        <div
                          className="mt-2 flex items-center gap-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            id="foOnly"
                            className="h-3 w-3 accent-blue-600 cursor-pointer"
                            checked={foOnly}
                            onChange={(e) => setFoOnly(e.target.checked)}
                          />
                          <label
                            htmlFor="foOnly"
                            className="text-xs font-medium text-gray-600 cursor-pointer select-none"
                          >
                            F&O Stock Only
                          </label>
                        </div>

                          </div>
                      )}

                    </th>

                    {/* Header */}
                    {getTimeframesForType(activeType).map((field) => (
                      <th
                        key={field}
                        className={`sticky top-0 bg-gray-100 z-20 p-2 cursor-pointer`}
                        onClick={() => toggleSort(activeType, field)}
                      >
                        {field}
                        {sortConfigs[activeType]?.field === field &&
                          (sortConfigs[activeType].direction === 'asc' ? ' ↑' : ' ↓')}
                          <div className="text-xs">
                            <span className="text-green-600">+{globalCounts[field].pos}</span>
                            {' / '}
                            <span className="text-red-600">-{globalCounts[field].neg}</span>
                          </div>

                        <div
                          className="mt-1 flex items-center gap-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <select
                            className="border rounded px-1 py-0.5 text-xs"
                            value={filterDrafts[field]?.op ?? '>'}
                            onChange={(e) =>
                              setFilterDrafts(prev => ({
                                ...prev,
                                [field]: { ...(prev[field]||{}), op: e.target.value }
                              }))
                            }
                          >
                            <option value=">">&gt;</option>
                            <option value="<">&lt;</option>
                          </select>

                          <input
                            type="number"
                            step="0.01"
                            placeholder="%"
                            className="w-8 border rounded px-1 py-0.5 text-xs"
                            value={filterDrafts[field]?.value ?? ''}
                            onChange={(e) =>
                              setFilterDrafts(prev => ({
                                ...prev,
                                [field]: { ...(prev[field] || {}), value: e.target.value }
                              }))
                            }
                          />

                        </div>

                        <div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setFilters(prev => ({
                                ...prev,
                                [field]: {
                                  ...(prev[field] || { op: '>', value: '' }),
                                  ...(filterDrafts[field] || {})
                                }
                              }));
                            }}
                            className={`px-2 py-1 text-xs border rounded text-white transition-colors
                              ${filters[field]?.value
                                ? 'bg-blue-800 hover:bg-blue-700' // active state stays blue-700
                                : 'bg-blue-400 hover:bg-blue-700'} // inactive default
                            `}
                            title="Apply this column’s filter"
                          >
                            ✓
                          </button>

                          <button
                            className="px-2 py-1 text-xs border rounded bg-red-400 text-white hover:bg-red-700"
                            title="Clear this column’s filter"
                            onClick={(e) => {
                              e.stopPropagation();
                              setFilterDrafts(prev => ({
                                ...prev,
                                [field]: { op: '>', value: '' }  // reset draft
                              }));
                              setFilters(prev => ({
                                ...prev,
                                [field]: { op: '>', value: '' }  // reset active
                              }));
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      </th>
                    ))}

                  </tr>
                </thead>
                  <tbody>
                    {Object.entries(groupedByType[activeType] || {})
                      .filter(([industry, data]) => {
                        // ✅ mode filter for EQUITY only
                        if (activeType === "EQUITY") {
                          const isGroup = industry.startsWith("group_");
                          if (categoryView === "sector" && isGroup) return false;     // hide groups
                          if (categoryView === "holding" && !isGroup) return false;   // only groups
                        }

                        // existing: hide empty categories (after stock filters)
                        return (data.stocks || []).some(stockPassesFilters);
                      })
                      .sort(([keyA, a], [keyB, b]) => {
                      const cfg = sortConfigs[activeType] || { field: '1D', direction: 'desc' };
                      const { field, direction } = cfg;

                      if (field === 'Industry') {
                        // use the entry keys as the industry names; case-insensitive compare
                        const nameA = (formatIndustryName(keyA) || '').toLowerCase();
                        const nameB = (formatIndustryName(keyB) || '').toLowerCase();
                        return direction === 'asc'
                          ? nameA.localeCompare(nameB)
                          : nameB.localeCompare(nameA);
                      } else {
                        const key = weighted ? field : `${field}_N`;
                        const valA = parseFloat(a.weightedReturns[key]) || 0;
                        const valB = parseFloat(b.weightedReturns[key]) || 0;
                        return direction === 'asc' ? valA - valB : valB - valA;
                      }
                    })
                    .map(([industry, data], index) => (
                      <React.Fragment key={`${activeType}-${industry}`}>
                        <tr className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="sticky left-0 bg-inherit z-10 p-2 w-[70px] min-w-[70px] max-w-[70px]">
                            <div className="flex items-center gap-2 text-blue-600">
                              <span>{index + 1}</span>

                            {/* Normal Industry */}
                            {activeType === "EQUITY" && !industry.startsWith("group_") && (
                              <a
                                href={`symbol/${encodeURIComponent(
                                  industry.toUpperCase().replace(/ /g, "_")
                                )}_SECTOR`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600"
                              >
                                <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                              </a>
                            )}

                            {/* Group */}
                            {activeType === "EQUITY" && industry.startsWith("group_") && (
                              <a
                                href={`/search/holding/${encodeURIComponent(
                                  industry.replace("group_", "")
                                )}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-blue-600"
                              >
                                <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5" />
                              </a>
                            )}
                            
                            </div>
                          </td>

                          <td
                            className={`sticky left-[70px] bg-inherit z-10 p-2 cursor-pointer text-blue-600 ${
                              isMobile ? "min-w-[200px] max-w-[200px]" : "min-w-[250px] max-w-[250px]"
                            }`}
                            onClick={() => toggleExpand(industry)}
                          >
                            {expanded?.[activeType]?.[industry] ? '− ' : '+ '}
                            {formatIndustryName(industry)}{" "}
                            ({countFiltered(data.stocks)}/{data.stocks.length})
                        </td>


                          {/* Data rows */}
                          {getTimeframesForType(activeType).map((field) => (
                              <td key={field} style={getColorStyle(data.weightedReturns[weighted ? field : field + '_N'])}>
                              {data.weightedReturns[weighted ? field : field + '_N'] || '-'}
                            </td>
                          ))}
                        </tr>

                      {expanded?.[activeType]?.[industry] &&
                        [...data.stocks]
                          .filter(stockPassesFilters)   // ← add this line
                          .sort((a, b) => {
                            const field = sortConfigs[activeType]?.field || "1D";
                            const valA = parseFloat(a.dummyData[field]) || 0;
                            const valB = parseFloat(b.dummyData[field]) || 0;
                            return sortConfigs[activeType]?.direction === "asc" ? valA - valB : valB - valA;
                          })
                          .map((stock) => (
                            <tr key={stock.symbol} className="bg-blue-50 border-t">
                              <td className="sticky left-0 bg-blue-50 z-10 p-2 w-[70px] min-w-[70px] max-w-[70px]">
                                <Sparklines data={stock.sparklineData} height={20} width={100}>
                                  <SparklinesLine color="blue" style={{ strokeWidth: 2, fill: "none" }} />
                                </Sparklines>
                              </td>

                              <td
                                className={`symbol-cell sticky left-[70px] bg-blue-50 z-10 p-2 ${
                                  isMobile ? "min-w-[200px] max-w-[200px]" : "min-w-[250px] max-w-[250px]"
                                }`}
                              >
                                <a
                                  href={`symbol/${stock.symbol}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 underline"
                                >
                                  {stock.name}
                                </a>
                                  {" "}
                                  {`(CMP ${getCurrencySymbol(stock.currency)}${
                                    activeType === 'USD' || activeType === 'GLOBAL_STOCKS'
                                      ? (stock.price ?? 0).toFixed(3)
                                      : (stock.price ?? 0).toFixed(2)
                                  })`}
                                  {activeType === 'EQUITY' && (                                
                                    <>
                                    <br />
                                      {"PE " + (stock.pe ?? 0).toFixed(2) +
                                      " | ROE " + ((stock.roe ?? 0) * 100).toFixed(2) + "%" +
                                      " | Mcap " +  `₹${new Intl.NumberFormat("en-IN", {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                      }).format(parseFloat(stock.marketCap) / 1e7)} Cr`}
                                    <br />
                                    {Array.isArray(stock.events) && stock.events.length > 0 && (
                                      <div className="text-gray-500 text-sm mt-1">
                                        {stock.events.map((e, idx) => (
                                          <div key={idx}>📌 {e.purpose} - {formatDate(e.date)}</div>
                                        ))}
                                      </div>
                                    )}
                                    </>
                                  )}
                                  {activeType === 'GLOBAL_STOCKS' && (
                                    <>
                                      <br />
                                      {"PE " + Number(stock.pe ?? 0).toFixed(2) +
                                        " | ROE " + Number((stock.roe ?? 0) * 100).toFixed(2) + "%" +
                                        " | Mcap " +
                                        `${getCurrencySymbol(stock.currency)}${new Intl.NumberFormat("en-US", {
                                          minimumFractionDigits: 2,
                                          maximumFractionDigits: 2,
                                        }).format(Number(stock.marketCap ?? 0) / 1e9)} B`
                                      }
                                    </>
                                  )}
                              </td>
                                {getTimeframesForType(activeType).map((field) => (
                                <td key={field} style={getColorStyle(stock.dummyData[field])}>
                                    {stock.dummyData[field] || '-'}
                                </td>
                              ))}
                            </tr>
                          ))}
                      </React.Fragment>
                    ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
    <Footer/>
    </>
  );
}

export default Dashboard;
